// Flag-gated entry point for the Hypothesis Workspace. Renders nothing unless
// the hypothesis_engine flag is on (default off), so it is a no-op for everyone
// by default and leaves the existing intake path untouched. On completion it
// stashes the card via the existing hypothesisSession handoff. No DB.

import { Suspense, lazy, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { useHypothesisEngineGate } from '../../config/useHypothesisEngineGate';
import { setCurrentHypothesis } from '../../engine/validation/hypothesisSession';

// Lazy-load the workspace (and with it the whole Hypothesis Engine: axes,
// synthesizer, research-memory repo) so the default-OFF majority does not ship
// it in the main bundle — the chunk is fetched only when a user actually opens
// the workspace. (The builder page is eager, so a static import would otherwise
// pull the engine into the main entry chunk.)
const HypothesisWorkspace = lazy(() => import('./HypothesisWorkspace'));

export default function HypothesisWorkspaceLauncher() {
  const gate = useHypothesisEngineGate();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);

  if (!gate.enabled) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
      >
        <Lightbulb className="w-3.5 h-3.5" />
        Start with a hypothesis
      </button>
      {done && (
        <div className="text-[11px] text-emerald-700 mt-1">
          Hypothesis saved — build your strategy, then run a backtest to get a Reality Check.
        </div>
      )}
      {open && (
        <Suspense fallback={null}>
          <HypothesisWorkspace
            onClose={() => setOpen(false)}
            onComplete={(card) => { setCurrentHypothesis(card); setDone(true); setOpen(false); }}
          />
        </Suspense>
      )}
    </div>
  );
}
